import cx from 'classnames'
import {
  ExternalIntegration,
  ExternalIntegrationEnum,
  ExternalIntegrationsClient,
} from 'api/clients/ExternalIntegrationsClient'
import { SUCCESS_RESPONSE_TYPE } from 'api/constants'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { useQuery, UseQueryOptions } from 'react-query'
import Styles from '../ZapierModalContent.module.scss'
import useResponsiveDesign from 'hooks/useResponsiveDesign'

import { ExportOption } from './ExportOption'

import { ModalStateEnum } from '../Types'
import { Button } from 'blaze-ui/Button'
import { ArticleMeta } from 'types'
import ArrowSwitchHorizontal from 'blaze-ui/icons/20/ArrowSwitchHorizontal'
import { CreateNewPostModal } from 'common/Prose/components/TextInlineFormatBar/components/CreateNewPost/CreateNewPostModal'
import { useModals } from 'almanac-ui/Modal'
import { useAlmanacEditorState } from 'stores/EditorState'
import { ZapierLogo } from 'almanac-ui/icons/24'
import { Download, TurnInto } from 'blaze-ui/icons'
import { getDocumentFromJson } from 'proseShared/utils/state'
import { schema } from 'prose-core'
import { TurnIntoWizard } from 'components/Wizards/TurnInto/TurnIntoWizard'
import { markdownSerializer } from 'common/Prose/components/TextInlineFormatBar/components/FormatBars/markdownSerializer'
import { SourceType } from 'components/Wizards/TurnInto/Source/Types'
import { ZapierIntegration } from 'components/IntegrationsModal/components/ZapierIntegration/ZapierIntegration'
import { usePaywallGuard } from 'hooks/usePaywallGuard'
import { useIsSystemRedesign } from 'hooks/useIsSystemRedesign'

export const fetchExternalIntegrations = async (workspaceId: number) => {
  const response = await ExternalIntegrationsClient.getExternalIntegrations({ workspaceId })

  if (response.type !== SUCCESS_RESPONSE_TYPE) {
    throw new Error(response.data)
  }

  return response.data
}

export interface Data {
  external_integrations: ExternalIntegration[]
}

export const useExternalIntegrations = (options?: UseQueryOptions<Data>) => {
  const workspaceId = useCurrentWorkspaceId()
  const query = useQuery<Data>(['externalIntegrations', workspaceId], () => fetchExternalIntegrations(workspaceId), {
    enabled: !!workspaceId,
    retry: false,
    retryOnMount: false,
    refetchOnWindowFocus: false,
    cacheTime: 1000 * 60 * 5,
    ...options,
  })

  const getIntegration = (provider: ExternalIntegrationEnum) => {
    return query.data?.external_integrations?.find((integration) => integration.provider === provider)
  }

  return {
    ...query,
    getIntegration,
  }
}

export const isProviderEnabled = (provider: ExternalIntegrationEnum, integrations: ExternalIntegration[]) => {
  return !!integrations.find((integration) => integration.provider === provider)
}

export const ExternalIntegrations = ({
  setModalState,
  article,
}: {
  setModalState: (state: ModalStateEnum) => void
  article: ArticleMeta
}) => {
  const isSystemRedesignEnabled = useIsSystemRedesign()
  const { isMobile } = useResponsiveDesign()
  const { openModal } = useModals()
  const almanacEditorState = useAlmanacEditorState()

  const doc = getDocumentFromJson(article?.main_layer.elements, schema)

  const documentAsMarkdown = markdownSerializer.serialize(doc)

  const buttonVariant = isSystemRedesignEnabled ? 'secondary' : 'tertiary'

  const handlePress = () => {
    openModal(TurnIntoWizard, {
      sourceType: SourceType.File,
      fromDocProps: {
        documentAsMarkdown,
      },
    })
  }

  const guardedHandlePress = usePaywallGuard(handlePress)

  const guardedHandleCrossPost = usePaywallGuard(() =>
    openModal(CreateNewPostModal, {
      view: almanacEditorState?.view,
      postTitle: article.title,
      articleChannel: article.channel,
      article,
    }),
  )

  return (
    <div className={cx(Styles.cardContainer, { [Styles.isMobile]: isMobile })}>
      <ExportOption
        title="Export to file"
        description="HTML, Markdown, PDF"
        icon={<Download className={Styles.icon} size={25} color="var(--neutral-50)" />}
        isConnected={false}
        buttons={[
          <Button
            key="connect"
            variant={buttonVariant}
            size="lg"
            fullWidth
            onPress={() => setModalState(ModalStateEnum.ExportToFile)}
          >
            Select File Type
          </Button>,
        ]}
      />
      <ExportOption
        title="Cross Post"
        description="Post on a different social media platform"
        icon={<ArrowSwitchHorizontal className={Styles.icon} size={25} color="var(--neutral-50)" />}
        isConnected={false}
        buttons={[
          <Button key="connect" variant={buttonVariant} size="lg" fullWidth onPress={guardedHandleCrossPost}>
            Create Cross Post
          </Button>,
        ]}
      />
      <ExportOption
        title="Zapier"
        description="You can export to CMS, social media or ad managers"
        icon={<ZapierLogo size={25} />}
        isConnected={false}
        buttons={[
          <Button
            key="connect"
            variant={buttonVariant}
            size="lg"
            fullWidth
            onPress={() => openModal(ZapierIntegration, { articleIdToPublish: article.id })}
          >
            Open
          </Button>,
        ]}
      />
      <ExportOption
        title="Turn Into"
        description="Generate different types of content using Blaze AI"
        icon={<TurnInto size={25} />}
        isConnected={false}
        buttons={[
          <Button key="connect" variant={buttonVariant} size="lg" fullWidth onPress={guardedHandlePress}>
            Open
          </Button>,
        ]}
      />
    </div>
  )
}
